/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState } from 'react';
import { Popover, Divider, Grid, ListItem } from '@material-ui/core';
import { StyledMenu, StyledGroupName, StyledButton, DefaultListItem, StyledPopoverContainer, StyledCheckboxWrapper, StyledLegendCircle, StyledFooter, ButtonHoverStyle, StyledSearchBox, StyledStickyLabel, StyledAdditionalLabel, StyledStickyContainer, StyledDropdownItem } from './DropdownList.style';
import { ButtonStyle, Button } from "../../button";
import { FormControlCheckBox } from "../../inputs";
import { DropdownListItem } from './DropdownListItem';
import { DiagnosticDashboardHeaderConstants } from "../../diagnosticsDashboard/constants";
import { SearchBox } from "../../search-box";
/**
 * @param options [Required] used to display the options to the dropdown list.
 * @param selectedValues [Optional] used to display selected item in the dropdown.
 * @param onChange [Required] Callback method fired on Dropdown selected item change.
 * @param onSortChange [Optional] Callback method fired on Dropdown when sorting on dropdown with sortItem.
 * @param anchorOrigin [Optional] specify the location of the popover.
 * @param transformOrigin [Optional] specify the location of the popover.
 * @param placeHolder [Optional] used to display placeholder text, display when selected value is not passed.
 * @param iconClassName [Optional] class name for the customization of dropdown list icon.
 * @param isIconHidden [Optional] class name for the disabling of the dropdown list icon from appearing.
 * @param className [Optional]  used to apply any class on the dropdown list
 * @param footer [Optional] used to show footer in the dropdown list with buttons to apply selection
 * @param isAllEnabled [Optional] used to have All checkbox selection functionality in multiselect checkbox dropdown.
 * @param id [Optional] used to specify the id of the component.
 * @param translateFn [Optional] method used for translations
 * @param iconLeftClassName [Optional] class name for customized left iconed dropdown list
 * @param shouldHideDropdownValue [Optional] used to hide the placeholder for dropdown
 * @param stickyLabel [Optional] used to stick the label to top while scrolling
 * @param isSearchEnabled [Optional] used to enable the search Box
 * @param onSearch [Optional] is a callback method to handle Search
 * @param additionalOptions [Optional] is used to display the additional options
 * @param dropdownPopupRef [Optional] is used to reference dropdown pop-up
 * @param isDisabled [Optional] disables the component if true
 * @param highlightBackgroundColor [Optional] custom background color for highlighted dropdown items
 * @param shouldCloseOnSecondaryButtonClick [Optional] used to specify whether the dropdown should be closed on secondary button click in the footer
 * @param ariaLabel [Optional] used to specify the aria-label of the component for accessibility.
 */
export const DropdownList = (props) => {
    const [isSortOptionItemClicked, setIsSortOptionItemClicked] = useState(false);
    const [isSortOptionItemApplied, setIsSortOptionItemApplied] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [container, setContainer] = useState(null);
    const [selectedValue, setSelectedValue] = useState(props.selectedValues);
    const [searchValue, setSearchValue] = useState('');
    const All = props.translateFn
        ? props.translateFn(DiagnosticDashboardHeaderConstants.ALL)
        : DiagnosticDashboardHeaderConstants.ALL;
    const closeMenu = (event) => {
        setAnchorEl(null);
        setIsSortOptionItemClicked(isSortOptionItemApplied);
        event && event.preventDefault();
    };
    const resetSortOption = () => {
        setIsSortOptionItemClicked(false);
        setIsSortOptionItemApplied(false);
    };
    const onClick = (selectedOption) => {
        selectedOption.sortItem ? setIsSortOptionItemClicked(true) : resetSortOption();
        //In case of footer not invoking onChange callback on item select and handle through filterFooterProps buttons event
        if (props.footer) {
            setSelectedValue(selectedOption);
            return;
        }
        closeMenu();
        props.onChange(selectedOption);
    };
    const getListItems = (options, shouldAlignWithGroupName) => (jsx(DropdownListItem, { options: options, selectedValue: selectedValue, onClick: onClick, onSortChange: props.onSortChange, isSortOptionItemClicked: isSortOptionItemClicked, isSortOptionItemApplied: isSortOptionItemApplied, shouldAlignWithGroupName: shouldAlignWithGroupName }));
    const isAllCheckboxSelected = () => {
        const propsSelectedValue = props.selectedValues;
        return propsSelectedValue.size === props.options.length;
    };
    const onCheckboxClick = (id) => {
        const propsSelectedValue = props.selectedValues;
        const updatedCheckedNodes = new Set(propsSelectedValue);
        const options = props.options;
        if (id === All) {
            if (propsSelectedValue.size === props.options.length) {
                options.map((option) => updatedCheckedNodes.delete(option.id));
            }
            else {
                options.map((option) => updatedCheckedNodes.add(option.id));
            }
        }
        else {
            updatedCheckedNodes.has(id) ? updatedCheckedNodes.delete(id) : updatedCheckedNodes.add(id);
        }
        props.onChange(updatedCheckedNodes);
    };
    const onListItemClick = (event) => {
        event.preventDefault();
        onCheckboxClick(event.currentTarget.id);
    };
    const getCheckboxDropdown = () => {
        const propsSelectedValue = props.selectedValues;
        const options = props.options;
        return (jsx(React.Fragment, null,
            props.isAllEnabled && (jsx(ListItem, { css: DefaultListItem, onClick: onListItemClick, id: All },
                jsx(StyledCheckboxWrapper, { id: `${All}_checkbox_label_id` },
                    jsx(FormControlCheckBox, { id: All, isChecked: isAllCheckboxSelected(), label: All, value: All, onChange: () => onCheckboxClick(All) })))),
            options.map((option, index) => (jsx(StyledDropdownItem, { key: `${option.id}`, isHighlightListItem: option.isHighlighted, highlightBackgroundColor: props.highlightBackgroundColor, css: DefaultListItem, onClick: onListItemClick, id: option.id },
                jsx(StyledCheckboxWrapper, { id: `${option.label}_checkbox_label_id` },
                    jsx(FormControlCheckBox, { id: option.id, isChecked: propsSelectedValue.has(option.id), label: option.label, value: option.id, isReadOnly: true, isBold: option.isHighlighted })),
                option.legendColor && jsx(StyledLegendCircle, { legendColor: option.legendColor }))))));
    };
    const getDropdownMenuItems = () => {
        const mapDropdownListOptions = (options, groupName, shouldDisplayGroupDivider, shouldAlignChildrenWithGroupName) => (jsx(React.Fragment, { key: groupName },
            groupName && (jsx(React.Fragment, null,
                jsx(StyledGroupName, null, groupName),
                shouldDisplayGroupDivider && jsx(Divider, { orientation: "horizontal", className: "divider" }))),
            getListItems(options, shouldAlignChildrenWithGroupName)));
        const localOptions = props.options;
        let menuItems;
        if (localOptions.every(item => Array.isArray(item.options))) {
            menuItems = props.options.map(item => mapDropdownListOptions(item.options, item.groupName, item.shouldDisplayGroupDivider, item.shouldAlignChildrenWithGroupName));
        }
        else if (props.selectedValues instanceof Set) {
            menuItems = getCheckboxDropdown();
        }
        else {
            menuItems = mapDropdownListOptions(props.options);
        }
        return menuItems;
    };
    const getDropdownPopoverMenuElement = (event) => {
        setAnchorEl(event.currentTarget);
        setContainer(event.target.parentNode);
        setSelectedValue(props.selectedValues);
        event.preventDefault();
    };
    const onSecondaryButtonClick = (event) => {
        if (props.shouldCloseOnSecondaryButtonClick !== false) {
            closeMenu(event);
        }
        setSearchValue('');
        if (props.footer && props.footer.secondaryButton && props.footer.secondaryButton.onClick) {
            props.footer.secondaryButton.onClick(event);
        }
    };
    const onPrimaryButtonClick = (event) => {
        closeMenu();
        setSearchValue('');
        if (props.selectedValues instanceof Set) {
            if (props.footer && props.footer.primaryButton && props.footer.primaryButton.onClick) {
                props.footer.primaryButton.onClick(event);
            }
        }
        else if (selectedValue) {
            selectedValue.sortItem ? setIsSortOptionItemApplied(true) : setIsSortOptionItemApplied(false);
            props.onChange(selectedValue);
            if (props.footer && props.footer.primaryButton && props.footer.primaryButton.onClick) {
                props.footer.primaryButton.onClick(event);
            }
        }
    };
    const isPrimaryButtonDisabled = () => {
        const propsSelectedValue = props.selectedValues;
        return propsSelectedValue.size === 0;
    };
    const dropdownFilterFooter = (footerProps) => (jsx(Grid, { container: true, wrap: "nowrap", justify: "space-between", alignItems: "center", css: StyledFooter },
        footerProps.secondaryButton && (jsx(StyledButton, Object.assign({}, footerProps.secondaryButton, { onClick: onSecondaryButtonClick, style: footerProps.secondaryButton.style || ButtonStyle.Tertiary }))),
        footerProps.primaryButton && (jsx(StyledButton, Object.assign({}, footerProps.primaryButton, { disabled: footerProps.primaryButton.disabled &&
                props.selectedValues instanceof Set &&
                isPrimaryButtonDisabled(), onClick: onPrimaryButtonClick, style: footerProps.primaryButton.style || ButtonStyle.Primary })))));
    const handleSearch = () => {
        props.onSearch && props.onSearch(searchValue);
    };
    const handleSearchCancel = () => {
        setSearchValue('');
        props.onSearch && props.onSearch('');
    };
    const handleSearchInputChange = (event) => {
        setSearchValue(event.target.value);
        props.onSearch && props.onSearch(event.target.value);
    };
    const searchInputProps = {
        id: 'drop-down-list__search-input',
        onChange: handleSearchInputChange,
        value: searchValue
    };
    const dropDownSearchBox = () => (jsx(StyledSearchBox, null,
        jsx(SearchBox, { cancelButtonProps: { shouldHide: true }, searchInputProps: searchInputProps, onSearch: handleSearch, isLiveSearch: true, shouldShowCrossIcon: !!searchValue, onCancelIconClick: handleSearchCancel, translateFn: props.translateFn })));
    const dropDownAdditionalOptions = (additionalOptionsProps) => (jsx(Grid, { container: true, direction: "column" }, additionalOptionsProps.options.map((item, index) => (jsx(StyledAdditionalLabel, { key: `${item.label}_${item.value}`, id: `list-item-${item.label}`, isSelected: item.value === additionalOptionsProps.selectedValue, onClick: () => additionalOptionsProps.onClick(item.value) }, item.label)))));
    const defaultIconClass = anchorEl ? 'mi-icon-arrow-up' : 'mi-icon-arrow-down';
    const getPlaceholder = () => {
        const propsSelectedValue = props.selectedValues;
        let placeholder;
        if (props.shouldHideDropdownValue) {
            placeholder = '';
        }
        else if (props.selectedValues && !(props.selectedValues instanceof Set)) {
            placeholder = propsSelectedValue.label;
        }
        else {
            placeholder = props.placeHolder;
        }
        return placeholder;
    };
    const getAccessibleLabel = () => {
        if (props.ariaLabel) {
            return props.ariaLabel;
        }
        const placeholder = getPlaceholder();
        if (placeholder && placeholder.trim() !== '') {
            return placeholder;
        }
        return '';
    };
    return (jsx(React.Fragment, null,
        jsx(Button, { id: props.id, className: props.className, css: anchorEl ? ButtonHoverStyle : undefined, style: ButtonStyle.Flat, label: getPlaceholder(), iconRight: props.isIconHidden ? undefined : props.iconClassName || defaultIconClass, icon: props.iconLeftClassName ? props.iconLeftClassName : undefined, disabled: props.isDisabled, onClick: getDropdownPopoverMenuElement, ariaLabel: props.translateFn ? props.translateFn(getAccessibleLabel()) : getAccessibleLabel() }),
        anchorEl && (jsx(Popover, { open: Boolean(anchorEl), container: props.shouldWrapIntoContainer ? container : undefined, anchorEl: anchorEl, onClose: closeMenu, anchorOrigin: props.anchorOrigin, transformOrigin: props.transformOrigin },
            jsx(StyledPopoverContainer, { direction: "column", container: true, wrap: "nowrap", ref: props.dropdownPopupRef },
                props.isSearchEnabled && dropDownSearchBox(),
                jsx(StyledStickyContainer, { issticky: props.stickyLabel ? 1 : 0 },
                    props.additionalOptions && dropDownAdditionalOptions(props.additionalOptions),
                    props.stickyLabel && jsx(StyledStickyLabel, null, props.stickyLabel),
                    jsx(StyledMenu, { issticky: props.stickyLabel ? 1 : 0, shouldScroll: props.footer ? true : false }, getDropdownMenuItems())),
                jsx(Divider, null),
                props.footer && dropdownFilterFooter(props.footer))))));
};
DropdownList.defaultProps = {
    shouldWrapIntoContainer: true,
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    transformOrigin: { vertical: 'top', horizontal: 'right' }
};
//# sourceMappingURL=DropdownList.js.map